import { ApplicationState } from "store";
import { Reducer } from "redux";
import {
    DayModuleElement,
    LayoutApiResponse,
    SvgCalendarLayout,
    WeekModuleElement,
} from "ui/constants";
import {
    BorderOptions,
    BurdeFontResource,
    BurdeFontResponse,
    BurdeSectionResponse,
    CustomImage,
    DateMetaData,
    ImageDetails,
    MonthDivider,
    OwnerPage,
    OwnerPageResponse,
} from "burde";

enum ActionType {
    SetLayoutOptions = "SET_LAYOUT_OPTIONS",
    SetIsLoadingLayoutOptions = "SET_IS_LOADING_LAYOUT_OPTIONS",
    SetBorderOptions = "SET_BORDER_OPTIONS",
    SetOwnerPageOptions = "SET_OWNER_PAGE_OPTIONS",
    SetBurdeCoverSections = "SET_BURDE_COVER_SECTIONS",
    SetInitialFrontCoverSectionId = "SET_INITIAL_FRONT_COVER_SECTION_ID",
    SetInitialBackCoverSectionId = "SET_INITIAL_Back_COVER_SECTION_ID",
    SetSelectedLayout = "SET_SELECTED_LAYOUT",
    SetSelectedDayModuleElements = "SET_SELECTED_DAY_MODULE_ELEMENTS",
    SetSelectedWeekModuleElements = "SET_SELECTED_WEEK_MODULE_ELEMENTS",
    SetMetadata = "SET_METADATA",
    SetCoverImage = "SET_COVER_IMAGE",
    SetBackCoverImage = "SET_BACK_COVER_IMAGE",
    SetCustomCoverImage = "SET_CUSTOM_COVER_IMAGE",
    SetCustomBackImage = "SET_CUSTOM_BACK_IMAGE",
    SetMonthDividerInfo = "SET_MONTH_DIVIDER_INFO",
    SetBorderImage = "SET_BORDER_IMAGE",
    SetSelectedOwnerPage = "SET_SELECTED_OWNER_PAGE",
    SetOwnerPageImageUrl = "SET_OWNER_IMAGE_URL",
    SetFontOptions = "SET_FONT_OPTIONS",
    SetIsLoadingFontOptions = "SET_IS_LOADING_FONT_OPTIONS",
    SetSelectedFont = "SET_SELECTED_FONT",
}

interface SetLayoutOptionsAction {
    type: ActionType.SetLayoutOptions;
    layoutOptions: LayoutApiResponse | null;
}

interface SetIsLoadingLayoutOptionsAction {
    type: ActionType.SetIsLoadingLayoutOptions;
    isLoadingLayoutOptions: boolean;
}

interface SetBorderOptionsAction {
    type: ActionType.SetBorderOptions;
    borderOptions: BorderOptions | null;
}

interface SetOwnerPageOptionsAction {
    type: ActionType.SetOwnerPageOptions;
    ownerPageOptions: OwnerPageResponse | null;
}

interface SetBurdeCoverContentAction {
    type: ActionType.SetBurdeCoverSections;
    burdeCoverSections: BurdeSectionResponse;
}

interface SetInitialFrontCoverSectionId {
    type: ActionType.SetInitialFrontCoverSectionId;
    initialFrontCoverSectionId: number | null;
}

interface SetInitialBackCoverSectionId {
    type: ActionType.SetInitialBackCoverSectionId;
    initialBackCoverSectionId: number | null;
}

interface SetSelectedLayout {
    type: ActionType.SetSelectedLayout;
    selectedLayout: SvgCalendarLayout | null;
}

interface SetSelectedDayModuleElements {
    type: ActionType.SetSelectedDayModuleElements;
    selectedDayModuleElements: DayModuleElement[] | null;
}

interface SetSelectedWeekModuleElements {
    type: ActionType.SetSelectedWeekModuleElements;
    selectedWeekModuleElements: WeekModuleElement[] | null;
}

interface SetMetadata {
    type: ActionType.SetMetadata;
    metadata: Array<DateMetaData> | null;
}

interface SetCoverImage {
    type: ActionType.SetCoverImage;
    value: ImageDetails | null;
}

interface SetBackCoverImage {
    type: ActionType.SetBackCoverImage;
    value: ImageDetails | null;
}

interface SetCustomCoverImage {
    type: ActionType.SetCustomCoverImage;
    value: CustomImage | null;
}

interface SetCustomBackImage {
    type: ActionType.SetCustomBackImage;
    value: CustomImage | null;
}

interface SetMonthDividerInfo {
    type: ActionType.SetMonthDividerInfo;
    value: MonthDivider | null;
}

interface SetBorderImage {
    type: ActionType.SetBorderImage;
    value: ImageDetails | null;
}

interface SetSelectedOwnerPage {
    type: ActionType.SetSelectedOwnerPage;
    value: OwnerPage | null;
}

interface SetOwnerPageImageUrl {
    type: ActionType.SetOwnerPageImageUrl;
    value: string | null;
}

interface SetFontOptions {
    type: ActionType.SetFontOptions;
    value: BurdeFontResponse | null;
}

interface SetIsLoadingFontOptions {
    type: ActionType.SetIsLoadingFontOptions;
    value: boolean;
}

interface SetSelectedFont {
    type: ActionType.SetSelectedFont;
    value: BurdeFontResource | null;
}

type BurdeApiContentStateAction =
    | SetLayoutOptionsAction
    | SetIsLoadingLayoutOptionsAction
    | SetBorderOptionsAction
    | SetOwnerPageOptionsAction
    | SetBurdeCoverContentAction
    | SetInitialFrontCoverSectionId
    | SetInitialBackCoverSectionId
    | SetSelectedLayout
    | SetSelectedDayModuleElements
    | SetSelectedWeekModuleElements
    | SetMetadata
    | SetCoverImage
    | SetBackCoverImage
    | SetCustomCoverImage
    | SetCustomBackImage
    | SetMonthDividerInfo
    | SetBorderImage
    | SetSelectedOwnerPage
    | SetOwnerPageImageUrl
    | SetFontOptions
    | SetIsLoadingFontOptions
    | SetSelectedFont;

export const actionCreators = {
    updateLayoutOptions: (layoutOptions: LayoutApiResponse | null) =>
        <SetLayoutOptionsAction>{
            type: ActionType.SetLayoutOptions,
            layoutOptions,
        },
    updateIsLoadingLayoutOptions: (isLoadingLayoutOptions: boolean) =>
        <SetIsLoadingLayoutOptionsAction>{
            type: ActionType.SetIsLoadingLayoutOptions,
            isLoadingLayoutOptions,
        },
    updateBorderOptions: (borderOptions: BorderOptions | null) =>
        <SetBorderOptionsAction>{
            type: ActionType.SetBorderOptions,
            borderOptions,
        },
    updateOwnerPageOptions: (ownerPageOptions: OwnerPageResponse | null) =>
        <SetOwnerPageOptionsAction>{
            type: ActionType.SetOwnerPageOptions,
            ownerPageOptions,
        },
    updateBurdeCoverSections: (
        burdeCoverSections: BurdeSectionResponse | null,
    ) =>
        <SetBurdeCoverContentAction>{
            type: ActionType.SetBurdeCoverSections,
            burdeCoverSections,
        },
    updateInitialFrontCoverSectionId: (
        initialFrontCoverSectionId: number | null,
    ) =>
        <SetInitialFrontCoverSectionId>{
            type: ActionType.SetInitialFrontCoverSectionId,
            initialFrontCoverSectionId,
        },
    updateInitialBackCoverSectionId: (
        initialBackCoverSectionId: number | null,
    ) =>
        <SetInitialBackCoverSectionId>{
            type: ActionType.SetInitialBackCoverSectionId,
            initialBackCoverSectionId,
        },
    updateSelectedLayout: (selectedLayout: SvgCalendarLayout | null) =>
        <SetSelectedLayout>{
            type: ActionType.SetSelectedLayout,
            selectedLayout,
        },
    updateSelectedDayModuleElements: (
        selectedDayModuleElements: DayModuleElement[] | null,
    ) =>
        <SetSelectedDayModuleElements>{
            type: ActionType.SetSelectedDayModuleElements,
            selectedDayModuleElements,
        },
    updateSelectedWeekModuleElements: (
        selectedWeekModuleElements: WeekModuleElement[] | null,
    ) =>
        <SetSelectedWeekModuleElements>{
            type: ActionType.SetSelectedWeekModuleElements,
            selectedWeekModuleElements,
        },
    updateMetadata: (metadata: DateMetaData[] | null) =>
        <SetMetadata>{
            type: ActionType.SetMetadata,
            metadata,
        },
    updateCoverImage: (value: ImageDetails | null) =>
        <SetCoverImage>{
            type: ActionType.SetCoverImage,
            value,
        },
    updateBackCoverImage: (value: ImageDetails | null) =>
        <SetBackCoverImage>{
            type: ActionType.SetBackCoverImage,
            value,
        },
    updateCustomCoverImage: (value: CustomImage | null) =>
        <SetCustomCoverImage>{
            type: ActionType.SetCustomCoverImage,
            value,
        },
    updateCustomBackImage: (value: CustomImage | null) =>
        <SetCustomBackImage>{
            type: ActionType.SetCustomBackImage,
            value,
        },
    updateMonthDividerInfo: (value: MonthDivider | null) =>
        <SetMonthDividerInfo>{
            type: ActionType.SetMonthDividerInfo,
            value,
        },
    updateBorderImage: (value: ImageDetails | null) =>
        <SetBorderImage>{
            type: ActionType.SetBorderImage,
            value,
        },
    updateSelectedOwnerPage: (value: OwnerPage | null) =>
        <SetSelectedOwnerPage>{
            type: ActionType.SetSelectedOwnerPage,
            value,
        },
    updateOwnerPageImageUrl: (value: string | null) =>
        <SetOwnerPageImageUrl>{
            type: ActionType.SetOwnerPageImageUrl,
            value,
        },
    updateFontOptions: (value: BurdeFontResponse | null) =>
        <SetFontOptions>{
            type: ActionType.SetFontOptions,
            value,
        },
    updateIsLoadingFontOptions: (value: boolean) =>
        <SetIsLoadingFontOptions>{
            type: ActionType.SetIsLoadingFontOptions,
            value,
        },
    updateSelectedFont: (value: BurdeFontResource | null) =>
        <SetSelectedFont>{
            type: ActionType.SetSelectedFont,
            value,
        },
};

export type BurdeApiContentState = {
    layoutOptions: LayoutApiResponse | null;
    borderOptions: BorderOptions | null;
    ownerPageOptions: OwnerPageResponse | null;
    isLoadingLayoutOptions: boolean;
    burdeCoverSections: BurdeSectionResponse | null;
    initialFrontCoverSectionId: number | null;
    initialBackCoverSectionId: number | null;
    selectedLayout: SvgCalendarLayout | null;
    selectedDayModuleElements: DayModuleElement[] | null;
    selectedWeekModuleElements: WeekModuleElement[] | null;
    metadata: DateMetaData[] | null;
    coverImage: ImageDetails | null;
    backCoverImage: ImageDetails | null;
    customCoverImage: CustomImage | null;
    customBackImage: CustomImage | null;
    monthDividerInfo: MonthDivider | null;
    borderImage: ImageDetails | null;
    ownerPage: OwnerPage | null;
    ownerPageImageUrl: string | null;
    fontOptions: BurdeFontResponse | null;
    isLoadingFontOptions: boolean;
    selectedFont: BurdeFontResource | null;
};

export const getLayoutOptions = (state: ApplicationState) =>
    state.burdeApiContent.layoutOptions;

export const getBorderOptions = (state: ApplicationState) =>
    state.burdeApiContent.borderOptions;

export const getOwnerPageOptions = (state: ApplicationState) =>
    state.burdeApiContent.ownerPageOptions;

export const getIsLoadingLayoutOptions = (state: ApplicationState) =>
    state.burdeApiContent.isLoadingLayoutOptions;

export const getBurdeCoverSections = (state: ApplicationState) =>
    state.burdeApiContent.burdeCoverSections;

export const getInitialFrontCoverSectionId = (state: ApplicationState) =>
    state.burdeApiContent.initialFrontCoverSectionId;

export const getInitialBackCoverSectionId = (state: ApplicationState) =>
    state.burdeApiContent.initialBackCoverSectionId;

export const getSelectedLayout = (state: ApplicationState) =>
    state.burdeApiContent.selectedLayout;

export const getSelectedDayModuleElements = (state: ApplicationState) =>
    state.burdeApiContent.selectedDayModuleElements;

export const getSelectedWeekModuleElements = (state: ApplicationState) =>
    state.burdeApiContent.selectedWeekModuleElements;

export const getMetadata = (state: ApplicationState) =>
    state.burdeApiContent.metadata;

export const getCoverImage = (state: ApplicationState) =>
    state.burdeApiContent.coverImage;

export const getBackCoverImage = (state: ApplicationState) =>
    state.burdeApiContent.backCoverImage;

export const getCustomCoverImage = (state: ApplicationState) =>
    state.burdeApiContent.customCoverImage;

export const getCustomBackImage = (state: ApplicationState) =>
    state.burdeApiContent.customBackImage;

export const getMonthDividerInfo = (state: ApplicationState) =>
    state.burdeApiContent.monthDividerInfo;

export const getBorderImage = (state: ApplicationState) =>
    state.burdeApiContent.borderImage;

export const getOwnerPage = (state: ApplicationState) =>
    state.burdeApiContent.ownerPage;

export const getOwnerPageImageUrl = (state: ApplicationState) =>
    state.burdeApiContent.ownerPageImageUrl;

export const getFontOptions = (state: ApplicationState) =>
    state.burdeApiContent.fontOptions;

export const getIsLoadingFontOptions = (state: ApplicationState) =>
    state.burdeApiContent.isLoadingFontOptions;

export const getSelectedFont = (state: ApplicationState) =>
    state.burdeApiContent.selectedFont;

export const initialState: BurdeApiContentState = {
    layoutOptions: null,
    borderOptions: null,
    ownerPageOptions: null,
    isLoadingLayoutOptions: false,
    burdeCoverSections: null,
    initialFrontCoverSectionId: null,
    initialBackCoverSectionId: null,
    selectedLayout: null,
    selectedDayModuleElements: null,
    selectedWeekModuleElements: null,
    metadata: null,
    coverImage: null,
    backCoverImage: null,
    customCoverImage: null,
    customBackImage: null,
    monthDividerInfo: null,
    borderImage: null,
    ownerPage: null,
    ownerPageImageUrl: null,
    fontOptions: null,
    isLoadingFontOptions: false,
    selectedFont: null,
};

export const reducer: Reducer<BurdeApiContentState, any> = (
    state: BurdeApiContentState = initialState,
    action: BurdeApiContentStateAction,
) => {
    if (action && action.type === ActionType.SetLayoutOptions) {
        if (action.layoutOptions) {
            return { ...state, layoutOptions: action.layoutOptions };
        }
    }
    if (action && action.type === ActionType.SetBorderOptions) {
        if (action.borderOptions) {
            return { ...state, borderOptions: action.borderOptions };
        }
    }
    if (action && action.type === ActionType.SetOwnerPageOptions) {
        if (action.ownerPageOptions) {
            return { ...state, ownerPageOptions: action.ownerPageOptions };
        }
    }
    if (action && action.type === ActionType.SetIsLoadingLayoutOptions) {
        return {
            ...state,
            isLoadingLayoutOptions: action.isLoadingLayoutOptions,
        };
    }
    if (action && action.type === ActionType.SetBurdeCoverSections) {
        return {
            ...state,
            burdeCoverSections: action.burdeCoverSections,
        };
    }
    if (action && action.type === ActionType.SetInitialFrontCoverSectionId) {
        return {
            ...state,
            initialFrontCoverSectionId: action.initialFrontCoverSectionId,
        };
    }
    if (action && action.type === ActionType.SetInitialBackCoverSectionId) {
        return {
            ...state,
            initialBackCoverSectionId: action.initialBackCoverSectionId,
        };
    }
    if (action && action.type === ActionType.SetSelectedLayout) {
        return {
            ...state,
            selectedLayout: action.selectedLayout,
        };
    }
    if (action && action.type === ActionType.SetSelectedDayModuleElements) {
        return {
            ...state,
            selectedDayModuleElements: action.selectedDayModuleElements,
        };
    }
    if (action && action.type === ActionType.SetSelectedWeekModuleElements) {
        return {
            ...state,
            selectedWeekModuleElements: action.selectedWeekModuleElements,
        };
    }
    if (action && action.type === ActionType.SetMetadata) {
        return {
            ...state,
            metadata: action.metadata,
        };
    }
    if (action && action.type === ActionType.SetCoverImage) {
        return {
            ...state,
            coverImage: action.value,
        };
    }
    if (action && action.type === ActionType.SetBackCoverImage) {
        return {
            ...state,
            backCoverImage: action.value,
        };
    }
    if (action && action.type === ActionType.SetCustomCoverImage) {
        return {
            ...state,
            customCoverImage: action.value,
        };
    }
    if (action && action.type === ActionType.SetCustomBackImage) {
        return {
            ...state,
            customBackImage: action.value,
        };
    }
    if (action && action.type === ActionType.SetMonthDividerInfo) {
        return {
            ...state,
            monthDividerInfo: action.value,
        };
    }
    if (action && action.type === ActionType.SetBorderImage) {
        return {
            ...state,
            borderImage: action.value,
        };
    }
    if (action && action.type === ActionType.SetSelectedOwnerPage) {
        return {
            ...state,
            ownerPage: action.value,
        };
    }
    if (action && action.type === ActionType.SetOwnerPageImageUrl) {
        return {
            ...state,
            ownerPageImageUrl: action.value,
        };
    }
    if (action && action.type === ActionType.SetFontOptions) {
        return {
            ...state,
            fontOptions: action.value,
        };
    }
    if (action && action.type === ActionType.SetIsLoadingFontOptions) {
        return {
            ...state,
            isLoadingFontOptions: action.value,
        };
    }
    if (action && action.type === ActionType.SetSelectedFont) {
        return {
            ...state,
            selectedFont: action.value,
        };
    }

    return state;
};
